import type { DashboardResponse, UpcomingPayment } from '@esportsbudget/shared';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  CalendarDays,
  Plus,
  Repeat,
  TrendingUp,
  Users,
  Wallet as WalletIcon,
} from 'lucide-react';
import { Suspense, lazy, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { PayoutFormModal } from '@/components/payouts/PayoutFormModal';
import { TransactionFormModal } from '@/components/finance/TransactionFormModal';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, PageHeader } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Badge, EmptyState, Money, Skeleton } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useFormatters } from '@/lib/format';
import { useAuth } from '@/providers/AuthProvider';

/**
 * График движения денег грузится отдельно.
 *
 * Библиотека графиков — самая тяжёлая зависимость интерфейса, около трети
 * всей сборки. Держать её в основном куске значит заставлять каждого ждать
 * разбора кода ради одной карточки, до которой ещё нужно долистать.
 */
const CashflowChart = lazy(() =>
  import('@/components/dashboard/CashflowChart').then((m) => ({ default: m.CashflowChart })),
);

/**
 * Дашборд отвечает на три вопроса, ради которых сюда заходят:
 * сколько у меня денег, сколько я должен и в плюсе я или в минусе.
 */
export function DashboardPage() {
  const { t } = useTranslation(['dashboard', 'finance', 'payouts', 'common', 'nav']);
  const { workspace } = useAuth();
  const format = useFormatters();

  const [months, setMonths] = useState(6);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [transactionOpen, setTransactionOpen] = useState(false);

  const dashboardQuery = useQuery({
    queryKey: ['dashboard', workspace?.id, months],
    queryFn: () => api.get<DashboardResponse>('/dashboard', { months }),
    enabled: Boolean(workspace),
  });

  const data = dashboardQuery.data;
  const currency = workspace?.baseCurrency ?? 'RUB';
  const loading = dashboardQuery.isLoading;

  return (
    <>
      <PageHeader
        title={t('dashboard:title')}
        subtitle={data ? format.monthYear(data.kpi.periodLabel) : undefined}
        action={
          <>
            <Button
              variant="secondary"
              icon={<Plus />}
              onClick={() => setTransactionOpen(true)}
            >
              {t('dashboard:addTransaction')}
            </Button>
            <Button icon={<TrendingUp />} onClick={() => setPayoutOpen(true)}>
              {t('payouts:accrue')}
            </Button>
          </>
        }
      />

      {/* Просрочка — красный блок появляется только когда есть о чём кричать */}
      {(data?.overdue.length ?? 0) > 0 && (
        <Card className="mb-4 border-danger bg-danger-soft" padded={false}>
          <div className="flex flex-wrap items-center gap-3 p-4">
            <AlertTriangle className="h-5 w-5 shrink-0 text-danger" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-semibold text-danger">
                {t('dashboard:overdueTitle', { count: data!.overdue.length })}
              </p>
              <p className="mt-0.5 truncate text-[12.5px] text-danger/80">
                {data!.overdue
                  .slice(0, 3)
                  .map(
                    (item) =>
                      `${item.title} — ${format.money(item.amountMinor, item.currency)}`,
                  )
                  .join(' · ')}
                {data!.overdue.length > 3 && ` · ещё ${data!.overdue.length - 3}`}
              </p>
            </div>
            <Money className="shrink-0 text-[17px] font-semibold text-danger">
              {format.money(data!.kpi.overdueMinor, currency)}
            </Money>
            <Link to="/payouts">
              <Button variant="danger" size="sm">
                {t('dashboard:goToPayouts')}
              </Button>
            </Link>
          </div>
        </Card>
      )}

      {/* Ключевые цифры */}
      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          label={t('dashboard:totalBalance')}
          loading={loading}
          value={format.money(data?.kpi.totalBalanceMinor ?? 0, currency)}
          hint={
            data && data.kpi.balanceDeltaMinor !== 0 ? (
              <span className={data.kpi.balanceDeltaMinor > 0 ? 'text-success' : undefined}>
                {format.money(data.kpi.balanceDeltaMinor, currency, { signDisplay: 'always' })}{' '}
                {t('dashboard:thisMonth')}
              </span>
            ) : undefined
          }
        />

        <Kpi
          label={t('finance:income')}
          loading={loading}
          value={format.money(data?.kpi.incomeMinor ?? 0, currency)}
          tone="in"
        />

        <Kpi
          label={t('finance:expense')}
          loading={loading}
          value={format.money(data?.kpi.expenseMinor ?? 0, currency)}
          tone="out"
          hint={
            data && data.payroll.accruedMinor > 0
              ? t('dashboard:ofWhichPayroll', {
                  amount: format.money(data.payroll.accruedMinor, currency),
                })
              : undefined
          }
        />

        <Kpi
          label={t('dashboard:dueSoon')}
          loading={loading}
          value={format.money(data?.kpi.dueSoonMinor ?? 0, currency)}
          hint={
            (data?.kpi.overdueMinor ?? 0) > 0 ? (
              <span className="text-danger">
                {t('dashboard:includingOverdue', {
                  amount: format.money(data!.kpi.overdueMinor, currency),
                })}
              </span>
            ) : (
              t('dashboard:next30days')
            )
          }
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-[1.5fr_1fr]">
        {/* График */}
        <Card>
          <CardHeader
            title={t('dashboard:cashflow')}
            action={
              <Select
                options={[3, 6, 12].map((value) => ({
                  value: String(value),
                  label: t('dashboard:monthsCount', { count: value }),
                }))}
                value={String(months)}
                onChange={(event) => setMonths(Number(event.target.value))}
                className="h-8 py-1 text-[12.5px]"
              />
            }
          />
          {loading ? (
            <Skeleton className="h-[260px]" />
          ) : (
            <Suspense fallback={<Skeleton className="h-[260px]" />}>
              <CashflowChart data={data!.cashflow} currency={currency} />
            </Suspense>
          )}
        </Card>

        {/* Ближайшие выплаты */}
        <Card padded={false}>
          <div className="p-5 pb-3">
            <CardHeader
              title={t('dashboard:upcoming')}
              subtitle={t('dashboard:upcomingHint')}
              className="mb-0"
              action={
                <Link to="/payouts" className="text-[12px] text-accent hover:underline">
                  {t('common:all')}
                </Link>
              }
            />
          </div>

          {loading ? (
            <div className="flex flex-col gap-2 px-5 pb-5">
              {[0, 1, 2].map((index) => (
                <Skeleton key={index} className="h-11" />
              ))}
            </div>
          ) : (data?.upcoming.length ?? 0) === 0 ? (
            <EmptyState
              icon={<CalendarDays />}
              title={t('dashboard:nothingUpcoming')}
              description={t('dashboard:nothingUpcomingHint')}
              className="py-8"
            />
          ) : (
            <div className="divide-y divide-border">
              {data!.upcoming.map((item) => (
                <UpcomingRow key={`${item.kind}-${item.id}`} item={item} />
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        {/* Кошельки */}
        <Card padded={false}>
          <div className="p-5 pb-2">
            <CardHeader
              title={t('nav:wallets')}
              className="mb-0"
              action={
                <Link to="/wallets" className="text-[12px] text-accent hover:underline">
                  {t('common:all')}
                </Link>
              }
            />
          </div>

          {loading ? (
            <div className="flex flex-col gap-2 px-5 pb-5">
              {[0, 1].map((index) => (
                <Skeleton key={index} className="h-10" />
              ))}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {data!.wallets.slice(0, 5).map((wallet) => (
                <div key={wallet.id} className="flex items-center gap-3 px-5 py-2.5">
                  <span
                    className="h-7 w-1.5 shrink-0 rounded"
                    style={{ backgroundColor: wallet.color }}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13.5px] font-medium">{wallet.name}</p>
                    <p className="text-[11.5px] text-subtle">
                      {t(`finance:walletType.${wallet.type}`)}
                    </p>
                  </div>
                  <Money className="text-[13.5px] font-semibold">
                    {format.money(wallet.balanceMinor, wallet.currency)}
                  </Money>
                </div>
              ))}
            </div>
          )}

          {(data?.missingRates.length ?? 0) > 0 && (
            <p className="mx-5 mb-4 rounded-lg bg-warning-soft px-3 py-2 text-[12px] text-warning">
              {t('finance:missingRates', { list: data!.missingRates.join(', ') })}
            </p>
          )}
        </Card>

        {/* Расходы по категориям */}
        <Card>
          <CardHeader title={t('dashboard:expensesByCategory')} />
          {loading ? (
            <Skeleton className="h-40" />
          ) : (data?.expensesByCategory.length ?? 0) === 0 ? (
            <p className="py-6 text-center text-[13px] text-subtle">
              {t('dashboard:noExpensesYet')}
            </p>
          ) : (
            <div className="flex flex-col gap-2.5">
              {data!.expensesByCategory.map((slice) => (
                <div key={slice.categoryId ?? 'none'}>
                  <div className="mb-1 flex items-baseline justify-between gap-2 text-[13px]">
                    <span className="truncate">{slice.name}</span>
                    <Money className="shrink-0 font-medium">
                      {format.money(slice.amountMinor, currency)}
                    </Money>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-surface-2">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${slice.percent}%`, backgroundColor: slice.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Зарплатный фонд */}
        <Card>
          <CardHeader
            title={t('dashboard:payroll')}
            subtitle={data ? format.monthYear(data.kpi.periodLabel) : undefined}
            icon={<Users />}
          />
          {loading ? (
            <Skeleton className="h-32" />
          ) : data!.payroll.peopleCount === 0 ? (
            <p className="py-6 text-center text-[13px] text-subtle">
              {t('dashboard:noPayrollYet')}
            </p>
          ) : (
            <dl className="flex flex-col gap-2 text-[13.5px]">
              <Row
                label={t('dashboard:accruedTo', { count: data!.payroll.peopleCount })}
                value={format.money(data!.payroll.accruedMinor, currency)}
              />
              {data!.payroll.taxMinor > 0 && (
                <Row
                  label={t('dashboard:taxes')}
                  value={format.money(data!.payroll.taxMinor, currency)}
                />
              )}
              <Row
                label={t('dashboard:paidOut')}
                value={format.money(data!.payroll.paidMinor, currency)}
                tone="success"
              />
              <div className="mt-1 border-t border-border pt-2">
                <Row
                  label={t('dashboard:leftToPay')}
                  value={format.money(data!.payroll.remainderMinor, currency)}
                  bold
                />
              </div>
            </dl>
          )}
        </Card>
      </div>

      {/* Последние операции */}
      <Card className="mt-3" padded={false}>
        <div className="p-5 pb-2">
          <CardHeader
            title={t('dashboard:recent')}
            className="mb-0"
            action={
              <Link to="/transactions" className="text-[12px] text-accent hover:underline">
                {t('common:all')}
              </Link>
            }
          />
        </div>

        {loading ? (
          <div className="flex flex-col gap-2 px-5 pb-5">
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} className="h-10" />
            ))}
          </div>
        ) : (data?.recent.length ?? 0) === 0 ? (
          <EmptyState
            icon={<WalletIcon />}
            title={t('dashboard:noTransactions')}
            action={
              <Button icon={<Plus />} onClick={() => setTransactionOpen(true)}>
                {t('dashboard:addTransaction')}
              </Button>
            }
            className="py-8"
          />
        ) : (
          <div className="divide-y divide-border">
            {data!.recent.map((item) => (
              <div key={item.id} className="flex items-center gap-3 px-5 py-2.5">
                <span
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
                    item.direction === 'IN'
                      ? 'bg-success-soft text-success'
                      : 'bg-surface-2 text-muted',
                  )}
                >
                  {item.direction === 'IN' ? (
                    <ArrowDownLeft className="h-3.5 w-3.5" aria-hidden />
                  ) : (
                    <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                  )}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13.5px]">{item.title}</p>
                  <p className="text-[11.5px] text-subtle">
                    {format.date(item.occurredAt)} · {item.walletName}
                  </p>
                </div>

                <Money
                  className="shrink-0 text-[13.5px] font-medium"
                  tone={item.direction === 'IN' ? 'in' : 'out'}
                >
                  {item.direction === 'IN' ? '+' : '−'}
                  {format.money(item.amountMinor, item.currency)}
                </Money>
              </div>
            ))}
          </div>
        )}
      </Card>

      <PayoutFormModal
        open={payoutOpen}
        onClose={() => setPayoutOpen(false)}
        onSaved={() => void dashboardQuery.refetch()}
      />

      <TransactionFormModal
        open={transactionOpen}
        transaction={null}
        wallets={(data?.wallets ?? []).map((wallet) => ({
          id: wallet.id,
          name: wallet.name,
          type: wallet.type,
          currency: wallet.currency,
          openingBalanceMinor: 0,
          balanceMinor: wallet.balanceMinor,
          balanceInBaseMinor: wallet.balanceInBaseMinor,
          color: wallet.color,
          icon: 'wallet',
          includeInTotals: true,
          cryptoAddress: null,
          notes: null,
          isArchived: false,
          sortOrder: 0,
          transactionCount: 0,
          plannedOutMinor: 0,
          createdAt: '',
        }))}
        categories={[]}
        onClose={() => setTransactionOpen(false)}
        onSaved={() => void dashboardQuery.refetch()}
      />
    </>
  );
}

function Kpi({
  label,
  value,
  hint,
  tone,
  loading,
}: {
  label: string;
  value: string;
  hint?: React.ReactNode;
  tone?: 'in' | 'out';
  loading?: boolean;
}) {
  return (
    <Card>
      <p className="mb-1.5 text-[12px] text-muted">{label}</p>
      {loading ? (
        <Skeleton className="h-7 w-32" />
      ) : (
        <Money size="lg" tone={tone ?? 'plain'}>
          {value}
        </Money>
      )}
      {hint && !loading && <p className="mt-1 text-[12px] text-muted">{hint}</p>}
    </Card>
  );
}

function UpcomingRow({ item }: { item: UpcomingPayment }) {
  const { t } = useTranslation(['dashboard', 'payouts']);
  const format = useFormatters();

  const soon = item.daysUntilDue <= 3;

  return (
    <div className="flex items-center gap-3 px-5 py-2.5">
      <span
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
          item.kind === 'BILL' ? 'bg-surface-2 text-muted' : 'bg-accent-soft text-accent',
        )}
      >
        {item.kind === 'BILL' ? (
          <Repeat className="h-3.5 w-3.5" aria-hidden />
        ) : (
          <span className="text-[10px] font-semibold">
            {item.title.slice(0, 2).toUpperCase()}
          </span>
        )}
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-[13.5px] font-medium">{item.title}</p>
        <p className="truncate text-[11.5px] text-subtle">
          {format.date(item.dueDate)} · {format.relativeDate(item.dueDate)}
          {item.subtitle && ` · ${item.subtitle}`}
        </p>
      </div>

      {soon && !item.isOverdue && (
        <Badge tone="warning" className="shrink-0">
          {t('dashboard:soon')}
        </Badge>
      )}

      <Money className="shrink-0 text-[13.5px] font-semibold">
        {format.money(item.amountMinor, item.currency)}
      </Money>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
  tone,
}: {
  label: string;
  value: string;
  bold?: boolean;
  tone?: 'success';
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd
        className={cn(
          'tabular font-medium',
          bold && 'text-[15px] font-semibold',
          tone === 'success' && 'text-success',
        )}
      >
        {value}
      </dd>
    </div>
  );
}
